// 🎬📝 Event feedback (09-23, MOVIE_NIGHT_UX_HANDOFF §3.7): the credits card's
// POST and its once-gate, modeled line by line on utils/bugReport.js.
//
// The answers ride the interjection route as body.event_feedback (the
// backend's CFN-cap piggyback, handler.js: a branch right after bug_report),
// no new endpoint. The server whitelists the answers key by key against the
// event's own questions and the context through bugReports.cleanContext, so
// nothing extra put here is ever stored. The DB is the real once-gate (UPSERT
// on event_id + reporter); localStorage below is comfort so the card never
// comes back on this device.
//
// ⚠️ NEVER put a player secret, a recovery code or an API key in the object.
// The secret rides the BODY ROOT only, where the server validates it and
// never stores it.
import { API_BASE_URL } from '../config/api';
import { buildBugContext } from './bugReport';

export const FEEDBACK_SOURCE = 'event_feedback';
export const FEEDBACK_TEXT_MAX = 1000;

const seenKey = (gameId, eventId) => `event_feedback_seen_${gameId}_${eventId}`;
const doneKey = (gameId, eventId) => `event_feedback_done_${gameId}_${eventId}`;

// every read/write in try/catch: private mode, a full quota or a blocked
// origin must never take the card (or the page) down
const readFlag = (key) => { try { return window.localStorage.getItem(key) === '1'; } catch (e) { return false; } };
const writeFlag = (key) => { try { window.localStorage.setItem(key, '1'); } catch (e) { /* private mode */ } };

/** "I was there while it ran" (stamped WHILE the event runs: the outing and
 *  the screening keys vanish in the same tick as the wrap). */
export const markSeen = (gameId, eventId) => { if (gameId && eventId) writeFlag(seenKey(gameId, eventId)); };
export const wasSeen = (gameId, eventId) => !!(gameId && eventId) && readFlag(seenKey(gameId, eventId));
/** sent OR skipped: the card never returns on this device */
export const markDone = (gameId, eventId) => { if (gameId && eventId) writeFlag(doneKey(gameId, eventId)); };
export const isDone = (gameId, eventId) => !!(gameId && eventId) && readFlag(doneKey(gameId, eventId));

/** POST one card. Throws an Error with a user-ready message on failure. */
export async function submitEventFeedback({ gameId, playerId, playerSecret, eventId, template, answers, text }) {
  if (!gameId || !eventId) throw new Error('Nothing to send.');
  const cleanAnswers = {};
  Object.entries(answers || {}).forEach(([k, v]) => {
    if (typeof k === 'string' && k && (Number.isInteger(v) || (typeof v === 'string' && v))) cleanAnswers[k] = v;
  });
  const line = String(text || '').trim().slice(0, FEEDBACK_TEXT_MAX);
  const body = {
    game_id: gameId,
    event_feedback: {
      event_id: String(eventId),
      template: template ? String(template).slice(0, 60) : null,
      answers: cleanAnswers,
      text: line || null,
      context: await buildBugContext(FEEDBACK_SOURCE),
    },
  };
  if (playerId) {
    body.player_id = playerId;
    if (playerSecret) body.player_secret = playerSecret;
  }
  const response = await fetch(`${API_BASE_URL}/chat-game/interjection`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const json = await response.json().catch(() => ({}));
  if (!response.ok || !json.success) {
    throw new Error(json.error || 'That did not send. Try again in a moment.');
  }
  return json;
}
